import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../../../components/base/Card';
import { Button } from '../../../components/base/Button';
import { supabase } from '../../../lib/supabase';

export const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [currentPost, setCurrentPost] = useState<any>(null);
  const [relatedPosts, setRelatedPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [slug]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch current post
      const { data: post, error: postError } = await supabase
        .from('posts')
        .select('*')
        .eq('slug', slug)
        .eq('is_published', true)
        .single();

      if (postError) throw postError;
      if (post) {
        setCurrentPost(post);

        // Fetch related posts (same category, exclude current)
        const { data: related, error: relatedError } = await supabase
          .from('posts')
          .select('*')
          .eq('category', post.category)
          .neq('id', post.id)
          .eq('is_published', true)
          .limit(2);

        if (!relatedError && related) {
          setRelatedPosts(related);
        }
      }
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#C1121F]"></div>
      </div>
    );
  }

  if (!currentPost) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#161616] mb-4">Blog Yazısı Bulunamadı</h1>
          <p className="text-gray-600 mb-8">Aradığınız blog yazısı mevcut değil.</p>
          <Link to="/blog">
            <Button variant="primary">
              <i className="ri-arrow-left-line mr-2"></i>
              Blog'a Dön
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Breadcrumb */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-[#C1121F]">Anasayfa</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <Link to="/blog" className="text-gray-500 hover:text-[#C1121F]">Blog</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-[#C1121F]">{currentPost.title}</span>
          </nav>
        </div>
      </section>

      {/* Article Header */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <span className="bg-[#C1121F] text-white px-4 py-2 rounded-full text-sm font-medium">
                {currentPost.category}
              </span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-[#161616] text-center mb-8" style={{ fontFamily: '"Poppins", sans-serif' }}>
              {currentPost.title}
            </h1>
            <div className="flex items-center justify-center space-x-6 text-gray-600 mb-8">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gray-200 rounded-full mr-3"></div>
                <div>
                  <div className="font-medium">{currentPost.author}</div>
                  <div className="text-sm">{currentPost.author_role}</div>
                </div>
              </div>
              <div className="text-sm">
                {new Date(currentPost.created_at).toLocaleDateString('tr-TR', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </div>
              {currentPost.read_time && (
                <div className="text-sm">
                  <i className="ri-time-line mr-1"></i>
                  {currentPost.read_time}
                </div>
              )}
            </div>
            {currentPost.featured_image && (
              <img 
                src={currentPost.featured_image}
                alt={currentPost.title}
                className="w-full h-64 lg:h-96 object-cover object-top rounded-lg shadow-lg mb-8"
              />
            )}
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
              {/* Main Content */}
              <div className="lg:col-span-3">
                <div 
                  className="prose prose-lg max-w-none"
                  style={{
                    '--tw-prose-headings': '#161616',
                    '--tw-prose-links': '#C1121F',
                    '--tw-prose-bold': '#161616',
                    '--tw-prose-counters': '#C1121F',
                    '--tw-prose-bullets': '#C1121F',
                  } as any}
                  dangerouslySetInnerHTML={{ __html: currentPost.content }}
                />

                {/* Tags */}
                {currentPost.tags && currentPost.tags.length > 0 && (
                  <div className="mt-12 pt-8 border-t">
                    <h3 className="text-lg font-semibold text-[#161616] mb-4">Etiketler</h3>
                    <div className="flex flex-wrap gap-2">
                      {currentPost.tags.map((tag: string, index: number) => (
                        <span 
                          key={index}
                          className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full hover:bg-[#C1121F] hover:text-white transition-colors cursor-pointer"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Share */}
                <div className="mt-8 pt-8 border-t">
                  <h3 className="text-lg font-semibold text-[#161616] mb-4">Paylaş</h3>
                  <div className="flex space-x-4">
                    <button className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors">
                      <i className="ri-facebook-fill"></i>
                    </button>
                    <button className="w-10 h-10 bg-blue-400 text-white rounded-full flex items-center justify-center hover:bg-blue-500 transition-colors">
                      <i className="ri-twitter-fill"></i>
                    </button>
                    <button className="w-10 h-10 bg-blue-700 text-white rounded-full flex items-center justify-center hover:bg-blue-800 transition-colors">
                      <i className="ri-linkedin-fill"></i>
                    </button>
                    <button className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center hover:bg-green-700 transition-colors">
                      <i className="ri-whatsapp-fill"></i>
                    </button>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-8">
                  {/* Related Posts */}
                  {relatedPosts.length > 0 && (
                    <Card className="mb-8">
                      <h3 className="text-lg font-semibold text-[#161616] mb-4">İlgili Yazılar</h3>
                      <div className="space-y-4">
                        {relatedPosts.map((post) => (
                          <Link key={post.id} to={`/blog/${post.slug}`} className="block group">
                            <div className="flex space-x-3">
                              {post.featured_image && (
                                <img 
                                  src={post.featured_image}
                                  alt={post.title}
                                  className="w-16 h-16 object-cover object-top rounded-lg"
                                />
                              )}
                              <div className="flex-1">
                                <h4 className="text-sm font-medium text-[#161616] group-hover:text-[#C1121F] transition-colors line-clamp-2">
                                  {post.title}
                                </h4>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </Card>
                  )}

                  {/* CTA */}
                  <Card className="bg-[#C1121F] text-white">
                    <h3 className="text-lg font-semibold mb-4">Teknik Destek Gerekiyor mu?</h3>
                    <p className="text-white/90 mb-4 text-sm">
                      Uzman ekibimizden proje danışmanlığı alın.
                    </p>
                    <Link to="/iletisim">
                      <Button variant="secondary" size="sm" className="w-full">
                        <i className="ri-phone-line mr-2"></i>
                        İletişime Geç
                      </Button>
                    </Link>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-between items-center">
              <Link to="/blog" className="flex items-center text-[#C1121F] hover:text-[#161616] transition-colors">
                <i className="ri-arrow-left-line mr-2"></i>
                Tüm Yazılar
              </Link>
              <Link to="/blog" className="flex items-center text-[#C1121F] hover:text-[#161616] transition-colors">
                Sonraki Yazı
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
